import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ProPaywall from '../components/ProPaywall';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  TrendingUp, TrendingDown, Minus, Shield, Clock, ExternalLink,
  AlertTriangle, Zap, ChevronDown, RefreshCw, Crown, Loader2, Bot, Globe
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sentimentConfig = {
  bullish: {
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    glow: 'shadow-emerald-500/5',
    icon: TrendingUp,
    label: 'Bullish',
    barColor: 'bg-emerald-500',
  },
  bearish: {
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    glow: 'shadow-red-500/5',
    icon: TrendingDown,
    label: 'Bearish',
    barColor: 'bg-red-500',
  },
  neutral: {
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/5',
    icon: Minus,
    label: 'Neutral',
    barColor: 'bg-blue-500',
  },
};

const impactConfig = {
  high: { color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'High Impact' },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Medium Impact' },
  low: { color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Low Impact' },
};

const ConfidenceBadge = ({ confidence }) => {
  const pct = Math.round(confidence * 100);
  let color = 'text-gray-400';
  let ringColor = 'border-gray-600';
  if (pct >= 70) { color = 'text-emerald-400'; ringColor = 'border-emerald-500'; }
  else if (pct >= 45) { color = 'text-yellow-400'; ringColor = 'border-yellow-500'; }
  else { color = 'text-red-400'; ringColor = 'border-red-500'; }

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${ringColor} bg-black/40`}
      data-testid="confidence-badge"
      title={`Confidence: ${pct}% — based on source diversity, sentiment agreement, and recency`}
    >
      <Shield size={12} className={color} />
      <span className={`text-xs font-bold ${color}`}>{pct}%</span>
    </div>
  );
};

const ConsensusBadge = ({ consensus }) => {
  if (!consensus) return null;
  const [agree, total] = consensus.split('/').map(Number);
  const full = agree === total && total >= 2;
  const color = full ? 'text-[#C5A059]' : 'text-gray-400';
  const border = full ? 'border-[#C5A059]/50' : 'border-gray-600';
  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${border} bg-black/40`}
      data-testid="consensus-badge"
      title={`${agree} of ${total} independent AI models agree on this direction`}
    >
      <Bot size={12} className={color} />
      <span className={`text-xs font-bold ${color}`}>{consensus}</span>
    </div>
  );
};

const IntelCard = ({ item, onClick }) => {
  const s = sentimentConfig[item.sentiment] || sentimentConfig.neutral;
  const imp = impactConfig[item.impact] || impactConfig.medium;
  const SentimentIcon = s.icon;
  const timeAgo = getTimeAgo(item.created_at);

  return (
    <div
      onClick={() => onClick(item)}
      className={`group cursor-pointer bg-gradient-to-br from-[#111111] to-[#0A0A0A] border ${s.border} rounded-xl p-5 hover:shadow-lg ${s.glow} transition-all duration-300 hover:translate-y-[-2px]`}
      data-testid={`intel-card-${item.intel_id}`}
    >
      {/* Top row: sentiment + confidence + impact */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${s.bg}`}>
            <SentimentIcon size={13} className={s.color} />
            <span className={`text-xs font-bold uppercase tracking-wide ${s.color}`}>{s.label}</span>
          </div>
          <div className={`px-2 py-1 rounded-full ${imp.bg}`}>
            <span className={`text-xs font-semibold ${imp.color}`}>{imp.label}</span>
          </div>
        </div>
        <ConfidenceBadge confidence={item.confidence} />
      </div>

      {/* Consensus */}
      {item.consensus && (
        <div className="flex items-center gap-1.5 mb-2">
          <Bot size={11} className="text-[#C5A059]" />
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">
            {item.consensus} model consensus{item.avg_conviction ? ` · ${item.avg_conviction}% conviction` : ''}
          </span>
        </div>
      )}

      {/* Title */}
      <h3 className="font-manrope font-bold text-lg text-white mb-2 group-hover:text-[#C5A059] transition-colors line-clamp-2">
        {item.title}
      </h3>

      {/* Summary */}
      <p className="text-gray-400 text-sm leading-relaxed mb-3 line-clamp-3">
        {item.summary}
      </p>

      {/* Key Insight */}
      {item.key_insight && (
        <div className="bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-lg px-3 py-2 mb-3">
          <p className="text-[#C5A059] text-xs font-medium">
            <Zap size={11} className="inline mr-1" />
            {item.key_insight}
          </p>
        </div>
      )}

      {/* Bottom row: assets + sources + time */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {(item.key_assets || []).slice(0, 4).map((asset) => (
            <span key={asset} className="text-[10px] font-bold uppercase bg-white/5 text-gray-300 px-2 py-0.5 rounded">
              {asset}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>{item.article_count} sources</span>
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {timeAgo}
          </span>
        </div>
      </div>
    </div>
  );
};

const IntelDetail = ({ item, onClose }) => {
  const s = sentimentConfig[item.sentiment] || sentimentConfig.neutral;
  const SentimentIcon = s.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-[#0D0D0D] border ${s.border} rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6`}
        onClick={(e) => e.stopPropagation()}
        data-testid="intel-detail-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${s.bg}`}>
              <SentimentIcon size={14} className={s.color} />
              <span className={`text-sm font-bold ${s.color}`}>{s.label}</span>
            </div>
            <ConfidenceBadge confidence={item.confidence} />
            <ConsensusBadge consensus={item.consensus} />
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">&times;</button>
        </div>

        <h2 className="font-manrope font-bold text-2xl text-white mb-3">{item.title}</h2>
        <p className="text-gray-300 text-base leading-relaxed mb-5">{item.summary}</p>

        {/* Model Votes */}
        {item.model_votes?.length > 0 && (
          <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4 mb-5" data-testid="model-votes-panel">
            <h4 className="text-gray-400 font-bold text-sm mb-2 flex items-center gap-1.5">
              <Bot size={14} className="text-[#C5A059]" /> Independent Model Votes
            </h4>
            <div className="space-y-1.5">
              {item.model_votes.map((v, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-mono">{v.model.split('/')[1] || v.model}</span>
                  <span className="flex items-center gap-2">
                    <span className={`font-bold uppercase ${v.sentiment === 'bullish' ? 'text-emerald-400' : v.sentiment === 'bearish' ? 'text-red-400' : 'text-gray-400'}`}>
                      {v.sentiment}
                    </span>
                    <span className="text-gray-600">{v.conviction}%</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Macro Backdrop */}
        {item.macro_pulse && (
          <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4 mb-5" data-testid="macro-backdrop-panel">
            <h4 className="text-gray-400 font-bold text-sm mb-2 flex items-center gap-1.5">
              <Globe size={14} className="text-[#C5A059]" /> Macro Backdrop
            </h4>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">
                Macro Pulse {item.macro_pulse.score > 0 ? '+' : ''}{item.macro_pulse.score} · {item.macro_pulse.regime}
              </span>
              {item.macro_alignment && item.macro_alignment !== 'unknown' && (
                <span className={`font-bold uppercase ${item.macro_alignment === 'aligned' ? 'text-emerald-400' : item.macro_alignment === 'opposed' ? 'text-red-400' : 'text-gray-400'}`}>
                  {item.macro_alignment === 'opposed' ? 'Fights macro — higher bar applied' : item.macro_alignment}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Technical Verification */}
        {item.tech_verification && item.sentiment !== 'neutral' && (
          <div className={`border rounded-lg p-4 mb-5 ${item.tech_verification.verified ? 'bg-emerald-400/5 border-emerald-400/25' : 'bg-white/[0.02] border-white/10'}`} data-testid="tech-verification-panel">
            <h4 className={`font-bold text-sm mb-2 flex items-center gap-1.5 ${item.tech_verification.verified ? 'text-emerald-400' : 'text-gray-400'}`}>
              <Shield size={14} /> Technical Verification {item.tech_verification.verified ? '— PASSED' : '— NOT VERIFIED'}
            </h4>
            <p className="text-gray-500 text-xs mb-2">
              {item.tech_verification.verified
                ? `Live structure confirms the call (${item.tech_verification.direction_score}/10 confluence).`
                : `Call held back from Track Record: ${item.tech_verification.reason}.`}
            </p>
            {item.tech_verification.supporting?.length > 0 && (
              <p className="text-xs"><span className="text-emerald-400 font-semibold">Supporting:</span> <span className="text-gray-400">{item.tech_verification.supporting.join(' · ')}</span></p>
            )}
            {item.tech_verification.conflicting?.length > 0 && (
              <p className="text-xs mt-1"><span className="text-red-400 font-semibold">Against:</span> <span className="text-gray-400">{item.tech_verification.conflicting.join(' · ')}</span></p>
            )}
          </div>
        )}

        {/* Key Insight */}
        {item.key_insight && (
          <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-lg p-4 mb-5">
            <h4 className="text-[#C5A059] font-bold text-sm mb-1 flex items-center gap-1.5">
              <Zap size={14} /> Key Insight
            </h4>
            <p className="text-gray-200 text-sm">{item.key_insight}</p>
          </div>
        )}

        {/* Risk Factors */}
        {item.risk_factors && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 mb-5">
            <h4 className="text-red-400 font-bold text-sm mb-1 flex items-center gap-1.5">
              <AlertTriangle size={14} /> Risk Factor
            </h4>
            <p className="text-gray-300 text-sm">{item.risk_factors}</p>
          </div>
        )}

        {/* Assets */}
        {item.key_assets?.length > 0 && (
          <div className="mb-5">
            <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Related Assets</h4>
            <div className="flex flex-wrap gap-2">
              {item.key_assets.map((a) => (
                <span key={a} className="text-xs font-bold bg-white/5 text-gray-200 px-3 py-1 rounded-full border border-white/10">
                  {a}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Source Articles */}
        {item.source_urls?.length > 0 && (
          <div>
            <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Source Articles ({item.article_count})</h4>
            <div className="space-y-2">
              {item.source_urls.map((src, i) => (
                <a
                  key={i}
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors group"
                >
                  <ExternalLink size={14} className="text-gray-500 mt-0.5 flex-shrink-0 group-hover:text-[#C5A059]" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-300 group-hover:text-white line-clamp-1">{src.title}</p>
                    <p className="text-xs text-gray-600">{src.source}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function getTimeAgo(isoString) {
  if (!isoString) return '';
  const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const IntelligenceFeed = () => {
  const { isPro, getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all'); // all, bullish, bearish, neutral

  useEffect(() => {
    if (isPro) fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  const fetchFeed = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/intelligence/feed?limit=30`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Feed fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isPro) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-20">
          <ProPaywall feature="AI Market Intelligence Feed" />
        </div>
        <Footer />
      </div>
    );
  }

  const filtered = filter === 'all' ? items : items.filter((i) => i.sentiment === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="intelligence-feed-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Crown size={16} className="text-[#C5A059]" />
              <span className="text-xs text-[#C5A059] font-semibold uppercase tracking-wider">Pro Feature</span>
            </div>
            <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
              AI Market <span className="text-[#C5A059]">Intelligence</span>
            </h1>
            <p className="text-gray-400 mt-2 text-sm">
              Real-time AI-curated crypto news analysis. Updated every 10 minutes.
            </p>
          </div>
          <Button
            onClick={fetchFeed}
            variant="outline"
            size="sm"
            className="border-white/20 text-gray-300 hover:text-white self-start md:self-auto"
            data-testid="refresh-feed-btn"
          >
            <RefreshCw size={14} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'bullish', 'bearish', 'neutral'].map((f) => {
            const isActive = filter === f;
            const config = f === 'all' ? null : sentimentConfig[f];
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all whitespace-nowrap ${
                  isActive
                    ? f === 'all'
                      ? 'bg-white/10 text-white border border-white/20'
                      : `${config.bg} ${config.color} border ${config.border}`
                    : 'bg-white/[0.03] text-gray-500 border border-transparent hover:border-white/10 hover:text-gray-300'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? `All (${items.length})` : `${f} (${items.filter(i => i.sentiment === f).length})`}
              </button>
            );
          })}
        </div>

        {/* Feed */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-[#C5A059] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Zap size={40} className="text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-400 mb-2">
              {items.length === 0 ? 'Intelligence feed is warming up...' : 'No results for this filter'}
            </h3>
            <p className="text-gray-600 text-sm">
              {items.length === 0
                ? 'The AI pipeline runs every 10 minutes. Check back shortly for the first intelligence summaries.'
                : 'Try a different filter to see more results.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((item) => (
              <IntelCard key={item.intel_id} item={item} onClick={setSelectedItem} />
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <IntelDetail item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}

      <Footer />
    </div>
  );
};

export default IntelligenceFeed;
